import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const SPECIAL_CHARS_REGEX = /[~!@#$%^&*()\-_=+\[\]{};:,.<>/?]/;

export interface PasswordRuleResults {
  minLength: boolean;
  maxLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  specialChar: boolean;
  uniqueChars: boolean;
  noName: boolean;
  noRepeating: boolean;
  noOldPasswordMatch: boolean;
}

/**
 * Evaluates a candidate password against all reset-password rules and
 * returns the pass/fail state of each individual rule. Useful for driving
 * a live "Password Requirements" checklist in the UI.
 */
export function evaluatePasswordRules(
  password: string,
  options: { firstName?: string; lastName?: string; oldPassword?: string } = {}
): PasswordRuleResults {
  const value = password || '';
  const firstName = (options.firstName || '').trim().toLowerCase();
  const lastName = (options.lastName || '').trim().toLowerCase();
  const oldPassword = options.oldPassword || '';
  const lowerValue = value.toLowerCase();

  const uniqueChars = new Set(value.split('')).size;

  let containsName = false;
  if (firstName && firstName.length > 0 && lowerValue.includes(firstName)) {
    containsName = true;
  }
  if (lastName && lastName.length > 0 && lowerValue.includes(lastName)) {
    containsName = true;
  }

  let hasMoreThanTwoRepeating = false;
  for (let i = 0; i < value.length - 2; i++) {
    if (value[i] === value[i + 1] && value[i + 1] === value[i + 2]) {
      hasMoreThanTwoRepeating = true;
      break;
    }
  }

  return {
    minLength: value.length >= 8,
    maxLength: value.length > 0 && value.length <= 255,
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /[0-9]/.test(value),
    specialChar: SPECIAL_CHARS_REGEX.test(value),
    uniqueChars: uniqueChars >= 5,
    noName: !containsName,
    noRepeating: !hasMoreThanTwoRepeating,
    noOldPasswordMatch: !oldPassword || value !== oldPassword
  };
}

export function passwordRulesAreValid(results: PasswordRuleResults): boolean {
  return Object.values(results).every(Boolean);
}

/**
 * Reactive Forms validator that enforces the full reset-password rule set.
 * `getContext` is called at validation time so it can reflect the latest
 * customer name / old-password field values as the user types.
 */
export function newPasswordValidator(
  getContext: () => { firstName?: string; lastName?: string; oldPassword?: string }
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';
    if (!value) {
      return null; // let Validators.required handle empty case
    }
    const results = evaluatePasswordRules(value, getContext());
    return passwordRulesAreValid(results) ? null : { passwordRules: results };
  };
}
